import cx from 'classnames';
import { useCallback, type FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SourceType } from '@trello/analytics-types';
import { Analytics } from '@trello/atlassian-analytics';
import { dontUpsell } from '@trello/browser';
import {
  freeBoardsOver,
  freeBoardsRemaining,
  isCloseToFreeBoardLimit,
} from '@trello/business-logic/organization';
import { useFeatureGate } from '@trello/feature-gate-client';
import { useWorkspaceId } from '@trello/id-context';
import { getScreenFromUrl } from '@trello/marketing-screens';
import type { ButtonProps } from '@trello/nachos/button';
import { Button } from '@trello/nachos/button';
import { PopoverPlacement, usePopover } from '@trello/nachos/popover';
import type { CreateBoardTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { CreateBoardPopover } from 'app/src/components/CreateBoardPopover';
import { useCreateBoardTileLargeFragment } from './CreateBoardTileLargeFragment.generated';

import * as styles from './CreateBoardTileLarge.module.less';

interface CreateBoardTileLargeProps extends ButtonProps {
  className?: string;
  analyticsSource?: SourceType;
}

export const CreateBoardTileLarge: FunctionComponent<
  CreateBoardTileLargeProps
> = ({ className, analyticsSource, ...props }) => {
  const { triggerRef, toggle, show, hide, popoverProps, push, pop } =
    usePopover({
      placement: PopoverPlacement.RIGHT_START,
    });

  const workspaceId = useWorkspaceId();

  const newBoard = useCallback(() => {
    toggle();
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'tile',
      actionSubjectId: 'createBoardTile',
      source: analyticsSource ?? getScreenFromUrl(),
      containers: {
        workspace: {
          id: workspaceId,
        },
      },
    });
  }, [analyticsSource, toggle, workspaceId]);

  const { data: workspace } = useCreateBoardTileLargeFragment({
    from: { id: workspaceId },
  });

  const boardsRemaining = workspace ? freeBoardsRemaining(workspace) : 0;
  const boardsOverLimit = workspace ? freeBoardsOver(workspace) : 0;
  const isApproachingLimit = workspace
    ? isCloseToFreeBoardLimit(workspace)
    : false;
  const shouldShowRemainingCount =
    !dontUpsell() && (isApproachingLimit || boardsOverLimit > 0);

  const { value: isRedesignEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  return (
    <>
      <Button
        appearance="subtle"
        className={cx(
          {
            [styles.boardTile]: true,
            [styles.newBoardTile]: isRedesignEnabled,
          },
          className,
        )}
        onClick={newBoard}
        ref={triggerRef}
        testId={getTestId<CreateBoardTestIds>('create-board-tile')}
        {...props}
      >
        <div className={styles.boardDetails}>
          <span>
            <FormattedMessage
              id="templates.board_list_item_add.create-new-board"
              defaultMessage="Create new board"
              description="Create new board button text"
            />
          </span>
          {shouldShowRemainingCount && (
            <small>
              <FormattedMessage
                id="templates.board_list_item_add.count-remaining"
                defaultMessage="{boardsRemaining} remaining"
                description="Remaining free boards count"
                values={{ boardsRemaining }}
              />
              {boardsOverLimit > 0 && (
                <span>
                  {' | '}
                  <FormattedMessage
                    id="templates.board_list_item_add.boards-over"
                    defaultMessage="{boardsOverLimit, plural, =1 {1 board over} other {# boards over}}"
                    description="Number of boards over the free board limit"
                    values={{ boardsOverLimit }}
                  />
                </span>
              )}
            </small>
          )}
        </div>
      </Button>
      <CreateBoardPopover
        analyticsSource="workspaceBoardsScreen"
        show={show}
        hide={hide}
        pop={pop}
        push={push}
        popoverProps={popoverProps}
        preSelectedWorkspaceId={workspaceId ?? undefined}
      />
    </>
  );
};
